import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import InputBase from '@mui/material/InputBase';
import { useTheme } from '@mui/material/styles';
import React, { useCallback, useRef } from 'react';

import { ALL_PAGES, MyReportsFilterState, MyReportsScope } from './myReportsListHelpers';
import { OptionItem } from './myReportsOptions';
import { MultiSelectDropdown } from '../../../components/molecules/FilterBar';
import {
  QuickToggleGroup,
  QuickToggleOption,
} from '../../../components/molecules/QuickToggleGroup';
import { STANDARD_FIELD_HEIGHT } from '../../../configs/ui-constants';

export interface MyReportsFilterBarProps {
  pageToggleOptions: QuickToggleOption[];
  scopeToggleOptions: QuickToggleOption[];
  ownerOptions: OptionItem[];
  /** Controlled: the bar owns no filter state of its own. */
  filters: MyReportsFilterState;
  onChange: (filters: MyReportsFilterState) => void;
  onReset: () => void;
  /** Hides the page toggles — the list is pinned to one page by its host. */
  isPageLocked?: boolean;
}

/**
 * Filters apply as you type or click. There is no Search button because there is
 * nothing to wait for — every row is already in memory.
 *
 * Page and group are quick toggles rather than dropdowns: both are switches
 * between a handful of known views, and a toggle costs one click where a
 * dropdown costs three (open, tick, close). Report Owner stays a dropdown
 * because its list is unbounded and multi-select.
 *
 * The bar is fully controlled. It used to seed local state from a prop once with
 * no sync effect, which is why resetting the filters could clear the rows while
 * leaving the inputs showing the old values.
 */
export const MyReportsFilterBar: React.FC<MyReportsFilterBarProps> = ({
  pageToggleOptions,
  scopeToggleOptions,
  ownerOptions,
  filters,
  onChange,
  onReset,
  isPageLocked = false,
}) => {
  const theme = useTheme();
  // Deliberately not `hasAnyActiveFilter`, which trims: whitespace doesn't filter
  // anything, but the user can still see it in the box and expects a way to
  // clear it.
  // A locked page is not something the user set, so it must not light up Clear.
  const hasInput =
    filters.name.length > 0 ||
    (!isPageLocked && filters.pageName !== ALL_PAGES) ||
    filters.ownerIds.length > 0 ||
    filters.scope !== 'all';

  // Clearing removes the Clear button itself, which would drop focus to <body>
  // and strand a keyboard user at the top of the document. Hand it back to the
  // search input — the control they were working in.
  const searchInputRef = useRef<HTMLInputElement>(null);
  const handleReset = useCallback(() => {
    onReset();
    searchInputRef.current?.focus();
  }, [onReset]);

  const update = useCallback(
    <K extends keyof MyReportsFilterState>(key: K, value: MyReportsFilterState[K]) => {
      onChange({ ...filters, [key]: value });
    },
    [filters, onChange]
  );

  // Escape clears, keeping a keyboard route to the reset now that Enter-to-search
  // is gone.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && hasInput) handleReset();
    },
    [hasInput, handleReset]
  );

  const inputSx = {
    border: `1px solid ${theme.palette.v4.filterInputBorder}`,
    borderRadius: 1,
    px: 1,
    height: STANDARD_FIELD_HEIGHT,
    boxSizing: 'border-box',
    fontSize: '0.75rem',
    bgcolor: theme.palette.v4.surface,
    '& input': { p: 0 },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
        p: 1.25,
        bgcolor: theme.palette.v4.surface,
        border: `1px solid ${theme.palette.v4.border}`,
        borderRadius: 1,
      }}
    >
      {/* `flex: 1` so this row fills the bar — without it the box shrinks to its
          toggles and the `ml: 'auto'` search cluster below has nothing to push
          against, leaving the search box mid-row instead of right-aligned. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, flex: 1 }}>
        <QuickToggleGroup
          label="Filter by report group"
          options={scopeToggleOptions}
          value={filters.scope}
          onChange={(value) => update('scope', value as MyReportsScope)}
        />

        {!isPageLocked && (
          <>
            {/* A rule between the two groups: they filter different things, and
                run together they read as one long row of unrelated options. */}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.25 }} />
            <QuickToggleGroup
              label="Filter by page"
              options={pageToggleOptions}
              value={filters.pageName}
              onChange={(value) => update('pageName', value)}
            />
          </>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          <MultiSelectDropdown
            label="Report Owner"
            options={ownerOptions}
            selected={filters.ownerIds}
            onChange={(val) => update('ownerIds', val)}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', ...inputSx, width: 260 }}>
            <SearchIcon sx={{ fontSize: 14, color: 'text.secondary', mr: 0.5 }} />
            <InputBase
              placeholder="Search reports"
              value={filters.name}
              onChange={(e) => update('name', e.target.value)}
              onKeyDown={handleKeyDown}
              inputRef={searchInputRef}
              inputProps={{ 'aria-label': 'Search reports by name, page or owner' }}
              sx={{ flex: 1, fontSize: '0.75rem', '& input': { p: 0 } }}
            />
          </Box>
          {hasInput && (
            <Button
              variant="text"
              size="small"
              startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              onClick={handleReset}
              aria-label="Clear all report filters"
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                color: theme.palette.v4.accent,
                height: STANDARD_FIELD_HEIGHT,
                minHeight: STANDARD_FIELD_HEIGHT,
                boxSizing: 'border-box',
                whiteSpace: 'nowrap',
              }}
            >
              Clear
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};
